/**
 * Maps an agent record to a UI-friendly shape.
 *
 * Works for both:
 *   - POST /v1/workspace/agent/list  → rows inside `data[]`
 *   - GET  /v1/workspace/agent/:id   → single record inside `body`
 *
 * The real API uses `uuid` as the primary key and camelCase fields.
 * snake_case fallbacks are kept for forward-compatibility.
 *
 * @param {Record<string, unknown>} raw
 * @returns {{
 *   id: string,
 *   name: string,
 *   type: string,
 *   category: string,
 *   description: string,
 *   status: string,
 *   scope: string,
 *   version: number | null,
 *   starters: string[],
 *   tags: string[] | null,
 *   vaultUuid: string | null,
 *   tenantId: string | null,
 *   owner: string | null,
 *   createdBy: string | null,
 *   model: string | null,
 *   image: string | null,
 *   signature: string | null,
 *   photoUrl: string,
 *   createdAt: string | null,
 *   updatedAt: string | null,
 * }}
 */
export const mapAgentListItemToUi = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return {
      id: '',
      name: '',
      type: '',
      category: '',
      description: '',
      status: '',
      scope: '',
      version: null,
      starters: [],
      tags: null,
      vaultUuid: null,
      tenantId: null,
      owner: null,
      createdBy: null,
      model: null,
      image: null,
      signature: null,
      photoUrl: '',
      createdAt: null,
      updatedAt: null,
    };
  }

  // The real API returns `uuid` as the primary key
  const id =
    (typeof raw.uuid === 'string' && raw.uuid) ||
    (typeof raw.agentUuid === 'string' && raw.agentUuid) ||
    (typeof raw.agent_uuid === 'string' && raw.agent_uuid) ||
    (typeof raw.id === 'string' && raw.id) ||
    '';

  return {
    id,
    name: typeof raw.name === 'string' ? raw.name : '',
    type:
      (typeof raw.type === 'string' && raw.type) ||
      (typeof raw.agentType === 'string' && raw.agentType) ||
      (typeof raw.agent_type === 'string' && raw.agent_type) ||
      '',
    category:
      (typeof raw.category === 'string' && raw.category) ||
      (typeof raw.agentCategory === 'string' && raw.agentCategory) ||
      (typeof raw.agent_category === 'string' && raw.agent_category) ||
      '',
    description:
      (typeof raw.description === 'string' ? raw.description : '') ||
      (typeof raw.agentDescription === 'string' ? raw.agentDescription : '') ||
      '',
    status: typeof raw.status === 'string' ? raw.status : '',
    scope: typeof raw.scope === 'string' ? raw.scope : '',
    version: typeof raw.version === 'number' ? raw.version : null,
    // Real API stores starters in metadata.conversation_starters; `starters` field is always []
    starters: (() => {
      const fromMeta = raw.metadata?.conversation_starters;
      if (Array.isArray(fromMeta) && fromMeta.length > 0) {
        return fromMeta.filter((s) => typeof s === 'string');
      }
      return Array.isArray(raw.starters) ? raw.starters.filter((s) => typeof s === 'string') : [];
    })(),
    tags: Array.isArray(raw.tags) ? raw.tags : null,
    vaultUuid:
      (typeof raw.vaultUuid === 'string' && raw.vaultUuid) ||
      (typeof raw.vault_uuid === 'string' && raw.vault_uuid) ||
      null,
    tenantId:
      (typeof raw.tenantId === 'string' && raw.tenantId) ||
      (typeof raw.tenant_id === 'string' && raw.tenant_id) ||
      null,
    owner: typeof raw.owner === 'string' ? raw.owner : null,
    createdBy:
      (typeof raw.createdBy === 'string' && raw.createdBy) ||
      (typeof raw.created_by === 'string' && raw.created_by) ||
      null,
    model: typeof raw.model === 'string' ? raw.model : null,
    image: typeof raw.image === 'string' ? raw.image : null,
    signature: typeof raw.signature === 'string' ? raw.signature : null,
    photoUrl: typeof raw.metadata?.imageUrl === 'string' ? raw.metadata.imageUrl : '',
    createdAt:
      (typeof raw.createdAt === 'string' && raw.createdAt) ||
      (typeof raw.created_at === 'string' && raw.created_at) ||
      null,
    updatedAt:
      (typeof raw.updatedAt === 'string' && raw.updatedAt) ||
      (typeof raw.updated_at === 'string' && raw.updated_at) ||
      null,
  };
};
